import React, { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  TextField,
  Slider,
  Switch } from
'@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  ViewColumn as ViewColumnIcon,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  TableChart as TableChartIcon } from
'@mui/icons-material';
import { useScenario, useEntityFilter, useEntityGroupFilter, usePriorScenario } from '../components/layout/Layout';
import api from '../services/api';
import '../styles/Dashboard.css';

// Available columns for dashboard customization
const AVAILABLE_COLUMNS = [
{ key: 'entityCode', label: 'Entity Code', defaultVisible: true },
{ key: 'entityName', label: 'Entity Name', defaultVisible: true },
{ key: 'region', label: 'Region', defaultVisible: true },
{ key: 'country', label: 'Country', defaultVisible: true },
{ key: 'pretaxBookIncome', label: 'Pretax Book Income', defaultVisible: true },
{ key: 'totalTaxExpense', label: 'Total Tax Expense', defaultVisible: true },
{ key: 'currentTaxRate', label: 'Current Tax Rate (%)', defaultVisible: true },
{ key: 'beginningDeferredTaxRate', label: 'Begin Deferred Tax Rate (%)', defaultVisible: false },
{ key: 'endingDeferredTaxRate', label: 'End Deferred Tax Rate (%)', defaultVisible: false },
{ key: 'averageFXRate', label: 'Average FX Rate', defaultVisible: false },
{ key: 'beginningSpotRate', label: 'Begin Spot Rate', defaultVisible: false },
{ key: 'endingSpotRate', label: 'End Spot Rate', defaultVisible: false },
{ key: 'temporaryAdjustments', label: 'Temporary Adjustments', defaultVisible: false },
{ key: 'permanentAdjustments', label: 'Permanent Adjustments', defaultVisible: false },
{ key: 'afterTaxAdjustments', label: 'After Tax Adjustments', defaultVisible: false }];


// Fallback analytics data - used only when API fails
const generateMockAnalytics = (scenario, entity) => {
  // Return minimal structure with zero values when API fails
  return {
    pretaxBookIncome: {
      current: 0,
      previous: 0,
      change: 0,
      changeAmount: 0
    },
    totalTaxExpense: {
      current: 0,
      previous: 0,
      change: 0,
      changeAmount: 0,
      breakdown: {
        currentTaxExpense: 0,
        deferredTaxExpense: 0
      }
    },
    effectiveTaxRate: {
      current: 0,
      previous: 0,
      change: 0
    },
    entityCount: 0,
    jurisdictionCount: 0,
    topJurisdictions: []
  };
};

// Fallback entity table data - used only when API fails
const generateMockEntityTable = (scenario, entity) => {
  // Return empty array when API fails - no fake data
  return [];
};

// Format currency values
const formatCurrency = (value) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numValue);
};

// Format percentage values
const formatPercentage = (value, decimals = 1) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  return `${numValue.toFixed(decimals)}%`;
};

// Calculate analytics from real calculation data
const calculateAnalyticsFromData = (adjustments, entityInputs, masterEntities, masterAdjustments, priorEntityInputs = [], priorAdjustments = []) => {
  // Calculate GAAP PRETAX INCOME from 'Pretax' section adjustments (like in TaxCalculations)
  const pretaxAdjustments = adjustments.filter((adj) => adj.AdjustmentType === 'Pretax');
  const totalPretaxIncome = pretaxAdjustments.reduce((sum, adj) => {
    const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
    return sum + amount;
  }, 0);

  // Calculate TOTAL TAX EXPENSE from adjustment sections (like in TaxCalculations)
  // Get adjustment totals by type
  const getAdjustmentTotalByType = (adjustments, type) => {
    return adjustments.
    filter((adj) => adj.AdjustmentType === type).
    reduce((sum, adj) => {
      const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
      return sum + amount;
    }, 0);
  };

  // Calculate components for TOTAL CURRENT TAX EXPENSE
  const taxEffectedTempTotal = getAdjustmentTotalByType(adjustments, 'Tax Effected Temporary');
  const returnBasisTaxAdjTotal = getAdjustmentTotalByType(adjustments, 'Tax Adj. Return');
  const provisionBasisTaxAdjTotal = getAdjustmentTotalByType(adjustments, 'Tax Adj. provision');
  const returnToProvisionAdjTotal = getAdjustmentTotalByType(adjustments, 'Tax Adj. RTP');

  // For now, use a simplified calculation based on available adjustment data
  // In a full implementation, this would follow the exact TaxCalculations logic
  const totalCurrentTax = Math.max(0,
  provisionBasisTaxAdjTotal + returnToProvisionAdjTotal + returnBasisTaxAdjTotal
  );

  // TOTAL DEFERRED TAX EXPENSE is currently 0 in calculations page
  const totalDeferredTax = 0;

  const totalTaxExpense = totalCurrentTax + totalDeferredTax;

  // Calculate CURRENT TAX RATE from EntityInput data (like in TaxCalculations)
  const currentTaxRates = entityInputs.map((input) => {
    const taxRate = parseFloat(input.CurrentTaxRate) || 0;
    // Convert to percentage if stored as decimal
    return taxRate > 1 ? taxRate : taxRate * 100;
  });

  // Use weighted average based on pretax income, or simple average if no pretax income
  const totalEntityPretaxIncome = entityInputs.reduce((sum, input) => {
    return sum + (parseFloat(input.PretaxBookIncome) || 0);
  }, 0);

  let effectiveTaxRate = 0;
  if (totalEntityPretaxIncome > 0) {
    // Weighted average by pretax income
    effectiveTaxRate = entityInputs.reduce((sum, input, index) => {
      const pretaxIncome = parseFloat(input.PretaxBookIncome) || 0;
      const weight = pretaxIncome / totalEntityPretaxIncome;
      return sum + currentTaxRates[index] * weight;
    }, 0);
  } else if (currentTaxRates.length > 0) {
    // Simple average if no pretax income data
    effectiveTaxRate = currentTaxRates.reduce((sum, rate) => sum + rate, 0) / currentTaxRates.length;
  }

  // Calculate prior scenario GAAP PRETAX INCOME from 'Pretax' section adjustments
  const priorPretaxAdjustments = priorAdjustments.filter((adj) => adj.AdjustmentType === 'Pretax');
  const priorTotalPretaxIncome = priorPretaxAdjustments.reduce((sum, adj) => {
    const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
    return sum + amount;
  }, 0);

  // Calculate prior scenario TOTAL TAX EXPENSE from adjustment sections
  const priorTaxEffectedTempTotal = getAdjustmentTotalByType(priorAdjustments, 'Tax Effected Temporary');
  const priorReturnBasisTaxAdjTotal = getAdjustmentTotalByType(priorAdjustments, 'Tax Adj. Return');
  const priorProvisionBasisTaxAdjTotal = getAdjustmentTotalByType(priorAdjustments, 'Tax Adj. provision');
  const priorReturnToProvisionAdjTotal = getAdjustmentTotalByType(priorAdjustments, 'Tax Adj. RTP');

  const priorTotalCurrentTax = Math.max(0,
  priorProvisionBasisTaxAdjTotal + priorReturnToProvisionAdjTotal + priorReturnBasisTaxAdjTotal
  );

  const priorTotalDeferredTax = 0; // Same as current calculations page
  const priorTotalTaxExpense = priorTotalCurrentTax + priorTotalDeferredTax;

  // Calculate prior scenario CURRENT TAX RATE from EntityInput data
  const priorCurrentTaxRates = priorEntityInputs.map((input) => {
    const taxRate = parseFloat(input.CurrentTaxRate) || 0;
    // Convert to percentage if stored as decimal
    return taxRate > 1 ? taxRate : taxRate * 100;
  });

  // Use weighted average based on pretax income, or simple average if no pretax income
  const priorTotalEntityPretaxIncome = priorEntityInputs.reduce((sum, input) => {
    return sum + (parseFloat(input.PretaxBookIncome) || 0);
  }, 0);

  let priorEffectiveTaxRate = 0;
  if (priorTotalEntityPretaxIncome > 0) {
    // Weighted average by pretax income
    priorEffectiveTaxRate = priorEntityInputs.reduce((sum, input, index) => {
      const pretaxIncome = parseFloat(input.PretaxBookIncome) || 0;
      const weight = pretaxIncome / priorTotalEntityPretaxIncome;
      return sum + priorCurrentTaxRates[index] * weight;
    }, 0);
  } else if (priorCurrentTaxRates.length > 0) {
    // Simple average if no pretax income data
    priorEffectiveTaxRate = priorCurrentTaxRates.reduce((sum, rate) => sum + rate, 0) / priorCurrentTaxRates.length;
  }

  // Calculate changes
  const pretaxIncomeChange = priorTotalPretaxIncome > 0 ?
  (totalPretaxIncome - priorTotalPretaxIncome) / priorTotalPretaxIncome * 100 : 0;
  const taxExpenseChange = priorTotalTaxExpense > 0 ?
  (totalTaxExpense - priorTotalTaxExpense) / priorTotalTaxExpense * 100 : 0;
  const etrChange = effectiveTaxRate - priorEffectiveTaxRate;

  // Get unique entities and regions
  const uniqueEntities = [...new Set(entityInputs.map((input) => input.EntityCode))];
  const uniqueRegions = [...new Set(masterEntities.map((entity) => entity.Region))].filter(Boolean);

  // Create top jurisdictions data by region using pretax book income from adjustments
  const jurisdictionData = {};

  // Group entities by region and calculate pretax book income from 'Pretax' adjustments
  entityInputs.forEach((input) => {
    const entity = masterEntities.find((e) => e.EntityCode === input.EntityCode);
    const region = entity?.Region || 'Unknown';

    if (!jurisdictionData[region]) {
      jurisdictionData[region] = {
        region,
        pretaxIncome: 0,
        entityCount: 0
      };
    }

    // Calculate pretax book income from 'Pretax' adjustments for this entity
    const entityPretaxAdjustments = adjustments.filter((adj) =>
    adj.EntityCode === input.EntityCode && adj.AdjustmentType === 'Pretax'
    );
    const entityPretaxIncome = entityPretaxAdjustments.reduce((sum, adj) => {
      const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
      return sum + amount;
    }, 0);

    jurisdictionData[region].pretaxIncome += entityPretaxIncome;
    jurisdictionData[region].entityCount += 1;
  });

  const topJurisdictions = Object.values(jurisdictionData).
  sort((a, b) => b.pretaxIncome - a.pretaxIncome).
  slice(0, 10);

  return {
    pretaxBookIncome: {
      current: totalPretaxIncome,
      previous: priorTotalPretaxIncome,
      change: pretaxIncomeChange,
      changeAmount: totalPretaxIncome - priorTotalPretaxIncome
    },
    totalTaxExpense: {
      current: totalTaxExpense,
      previous: priorTotalTaxExpense,
      change: taxExpenseChange,
      changeAmount: totalTaxExpense - priorTotalTaxExpense,
      breakdown: {
        currentTaxExpense: totalCurrentTax,
        deferredTaxExpense: totalDeferredTax
      }
    },
    effectiveTaxRate: {
      current: effectiveTaxRate,
      previous: priorEffectiveTaxRate,
      change: etrChange
    },
    entityCount: uniqueEntities.length,
    jurisdictionCount: uniqueRegions.length,
    topJurisdictions: topJurisdictions
  };
};

// Create entity table data from real data
const createEntityTableData = (entityInputs, adjustments, masterEntities, masterAdjustments) => {
  return entityInputs.map((input) => {
    const entity = masterEntities.find((e) => e.EntityCode === input.EntityCode);

    // Count adjustments for this entity
    const entityAdjustments = adjustments.filter((adj) => adj.EntityCode === input.EntityCode);
    const temporaryAdjs = entityAdjustments.filter((adj) => adj.AdjustmentType?.includes('Temporary')).length;
    const permanentAdjs = entityAdjustments.filter((adj) => adj.AdjustmentType?.includes('Permanent')).length;

    // Use real data from EntityInputs page
    const currentTaxRate = parseFloat(input.CurrentTaxRate) || 0;
    const beginningDeferredTaxRate = parseFloat(input.BeginningDeferredTaxRate) || 0;
    const endingDeferredTaxRate = parseFloat(input.EndingDeferredTaxRate) || 0;
    const averageFXRate = parseFloat(input.AverageFXRate) || 0;
    const beginningSpotRate = parseFloat(input.BeginningSpotRate) || 0;
    const endingSpotRate = parseFloat(input.EndingSpotRate) || 0;

    // Convert tax rate to percentage if needed
    const effectiveTaxRate = currentTaxRate > 1 ? currentTaxRate : currentTaxRate * 100;

    // Calculate pretax income from 'Pretax' adjustments for this entity
    const entityPretaxAdjustments = adjustments.filter((adj) =>
    adj.EntityCode === input.EntityCode && adj.AdjustmentType === 'Pretax'
    );
    const pretaxBookIncome = entityPretaxAdjustments.reduce((sum, adj) => {
      const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
      return sum + amount;
    }, 0);

    // Calculate tax expense from tax adjustment types for this entity
    const entityTaxAdjs = adjustments.filter((adj) =>
    adj.EntityCode === input.EntityCode && (
    adj.AdjustmentType === 'Tax Adj. Return' ||
    adj.AdjustmentType === 'Tax Adj. provision' ||
    adj.AdjustmentType === 'Tax Adj. RTP')
    );
    const totalTaxExpense = entityTaxAdjs.reduce((sum, adj) => {
      const amount = parseFloat(adj.USDAmount) || parseFloat(adj.Amount) || 0;
      return sum + amount;
    }, 0);

    return {
      id: input.id || input.EntityCode,
      entityCode: input.EntityCode,
      entityName: entity?.EntityName || input.EntityCode,
      region: entity?.Region || 'Unknown',
      country: entity?.Country || 'Unknown',
      pretaxBookIncome: pretaxBookIncome,
      totalTaxExpense: Math.max(0, totalTaxExpense), // Ensure non-negative
      currentTaxRate: effectiveTaxRate,
      beginningDeferredTaxRate: beginningDeferredTaxRate > 1 ? beginningDeferredTaxRate : beginningDeferredTaxRate * 100,
      endingDeferredTaxRate: endingDeferredTaxRate > 1 ? endingDeferredTaxRate : endingDeferredTaxRate * 100,
      averageFXRate: averageFXRate,
      beginningSpotRate: beginningSpotRate,
      endingSpotRate: endingSpotRate,
      allAdjustments: entityAdjustments.length,
      temporaryAdjustments: temporaryAdjs,
      permanentAdjustments: permanentAdjs,
      afterTaxAdjustments: 0 // Not available in current data structure
    };
  });
};

// KPI Card Component
const KPICard = ({ title, value, previousValue, change, changeAmount, icon, format = 'currency' }) => {
  const isPositive = change >= 0;
  const formatValue = (val) => {
    if (format === 'currency') return formatCurrency(val);
    if (format === 'percentage') return formatPercentage(val);
    if (format === 'number') return val.toLocaleString();
    return val;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ mr: 2, color: 'primary.main' }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
        
        <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
          {formatValue(value)}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isPositive ?
          <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} /> :

          <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
          }
          
          <Typography
            variant="body2"
            sx={{
              color: isPositive ? 'success.main' : 'error.main',
              fontWeight: 'medium'
            }}>

            {formatPercentage(Math.abs(change))} {isPositive ? 'increase' : 'decrease'}
          </Typography>
        </Box>
        
        {changeAmount &&
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {formatValue(Math.abs(changeAmount))} vs. previous scenario
          </Typography>
        }
      </CardContent>
    </Card>);

};

function Dashboard() {
  // Use existing layout contexts
  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();
  const { selectedEntityGroup } = useEntityGroupFilter();
  const { selectedPriorScenario } = usePriorScenario();

  // Local state
  const [analytics, setAnalytics] = useState(null);
  const [entityTableData, setEntityTableData] = useState([]);
  const [sortBy, setSortBy] = useState('pretaxBookIncome');
  const [sortOrder, setSortOrder] = useState('desc');
  const [analyticsView, setAnalyticsView] = useState('pretaxIncome');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [dashboardViewDialogOpen, setDashboardViewDialogOpen] = useState(false);
  const [dashboardView, setDashboardView] = useState(() => {
    // Load dashboard view preference from localStorage
    const savedView = localStorage.getItem('dashboard-view-preference');
    return savedView || 'entityTable';
  });
  const [analyticsConfig, setAnalyticsConfig] = useState(() => {
    // Load analytics configuration from localStorage
    const savedConfig = localStorage.getItem('dashboard-analytics-config');
    if (savedConfig) {
      return JSON.parse(savedConfig);
    }
    return {
      etrWaterfallTopCountries: 5,
      comparisonYears: 3,
      comparisonMetric: 'pretaxBookIncome',
      comparisonGroupBy: 'country',
      priorPeriodScenario: null,
      showStateDetails: true
    };
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Load user preferences from localStorage or use defaults
    const savedPreferences = localStorage.getItem('dashboard-column-preferences');
    if (savedPreferences) {
      return JSON.parse(savedPreferences);
    }
    // Default visible columns
    return AVAILABLE_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.defaultVisible;
      return acc;
    }, {});
  });
  const [entityInputs, setEntityInputs] = useState([]);

  // Fetch real analytics data from calculations page data
  const fetchAnalyticsData = async () => {
    if (!selectedScenario) return;


    setLoading(true);
    try {
      // Import adjustment type utilities
      const { adjustmentTypeToRouteMap } = await import('../utils/adjustmentTypeUtils');
      const adjustmentTypes = Object.keys(adjustmentTypeToRouteMap);

      // Fetch entity inputs for current scenario
      const entityInputsResponse = await api.get('/entity-models/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      const fetchedEntityInputs = Array.isArray(entityInputsResponse.data) ? entityInputsResponse.data : entityInputsResponse.data.data || [];
      setEntityInputs(fetchedEntityInputs);

      // Fetch prior scenario entity inputs and adjustments if prior scenario is selected
      let priorEntityInputs = [];
      let priorAdjustments = [];
      if (selectedPriorScenario) {
        try {
          const priorEntityInputsResponse = await api.get('/entity-models/input', {
            params: { scenarioCode: selectedPriorScenario.scenarioCode }
          });
          priorEntityInputs = Array.isArray(priorEntityInputsResponse.data) ? priorEntityInputsResponse.data : priorEntityInputsResponse.data.data || [];

          // Fetch prior scenario adjustments
          const priorAdjustmentPromises = [];
          for (const type of adjustmentTypes) {
            const routePath = adjustmentTypeToRouteMap[type];
            if (routePath) {
              const promise = api.get(`/adjustments/${routePath}`, {
                params: { scenarioCode: selectedPriorScenario.scenarioCode }
              }).
              then((response) => {
                const data = response.data;
                const adjustments = Array.isArray(data) ? data : data.data || [];
                return adjustments.map((adj) => ({
                  ...adj,
                  AdjustmentType: type
                }));
              }).
              catch((error) => {
                console.error(`Error fetching prior ${type} adjustments:`, error);
                return [];
              });
              priorAdjustmentPromises.push(promise);
            }
          }

          const priorResults = await Promise.allSettled(priorAdjustmentPromises);
          priorAdjustments = priorResults.
          filter((result) => result.status === 'fulfilled').
          flatMap((result) => result.value);

        } catch (error) {

          priorEntityInputs = [];
          priorAdjustments = [];
        }
      }

      // Fetch master entities for entity names and details
      const entitiesResponse = await api.get('/entity-models/master');
      const masterEntities = entitiesResponse.data.data || [];

      // Fetch master adjustments for adjustment names
      const adjustmentsResponse = await api.get('/adjustments/master');
      const masterAdjustments = adjustmentsResponse.data.data || [];

      // Fetch all adjustment data from different types
      const allPromises = [];

      for (const type of adjustmentTypes) {
        const routePath = adjustmentTypeToRouteMap[type];
        if (routePath) {
          const promise = api.get(`/adjustments/${routePath}`, {
            params: { scenarioCode: selectedScenario.scenarioCode }
          }).
          then((response) => {
            const data = response.data;
            const adjustments = Array.isArray(data) ? data : data.data || [];
            return adjustments.map((adj) => ({
              ...adj,
              AdjustmentType: type
            }));
          }).
          catch((error) => {
            console.error(`Error fetching ${type} adjustments:`, error);
            return [];
          });
          allPromises.push(promise);
        }
      }

      const results = await Promise.allSettled(allPromises);
      const allAdjustments = results.
      filter((result) => result.status === 'fulfilled').
      flatMap((result) => result.value);

      // Filter by selected entity if specified
      let filteredAdjustments = allAdjustments;
      let filteredEntityInputs = fetchedEntityInputs;
      let filteredPriorEntityInputs = priorEntityInputs;
      let filteredPriorAdjustments = priorAdjustments;

      const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
      const groupSelections = entityArr.filter((e) => e.startsWith('[Group] '));
      const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
      if (entityArr.length > 0) {
        let entityCodesToFilter = [...plainEntities];
        if (groupSelections.length > 0) {
          try {
            const groupsResponse = await api.get('/entity-groups');
            const groups = groupsResponse.data.data || [];
            groupSelections.forEach((sel) => {
              const groupName = sel.replace('[Group] ', '');
              const selectedGroup = groups.find((g) => g.name === groupName);
              if (selectedGroup?.entityList?.length > 0) {
                selectedGroup.entityList.forEach((entityId) => {
                  const entity = masterEntities.find((e) => e.id === entityId);
                  if (entity?.EntityCode) entityCodesToFilter.push(entity.EntityCode);
                });
              }
            });
          } catch (error) {
            console.error('Error filtering by entity group:', error);
          }
        }
        if (entityCodesToFilter.length > 0) {
          filteredAdjustments = allAdjustments.filter((adj) => entityCodesToFilter.includes(adj.EntityCode));
          filteredEntityInputs = fetchedEntityInputs.filter((input) => entityCodesToFilter.includes(input.EntityCode));
          filteredPriorEntityInputs = priorEntityInputs.filter((input) => entityCodesToFilter.includes(input.EntityCode));
          filteredPriorAdjustments = priorAdjustments.filter((adj) => entityCodesToFilter.includes(adj.EntityCode));
        }
      }

      // Calculate analytics from real data with prior scenario comparison
      const processedAnalytics = calculateAnalyticsFromData(
        filteredAdjustments,
        filteredEntityInputs,
        masterEntities,
        masterAdjustments,
        filteredPriorEntityInputs,
        filteredPriorAdjustments
      );

      // Create entity table data
      const entityTableData = createEntityTableData(
        filteredEntityInputs,
        filteredAdjustments,
        masterEntities,
        masterAdjustments
      );


      setAnalytics(processedAnalytics);
      setEntityTableData(entityTableData);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      // Set empty data structure when API fails
      const emptyAnalytics = generateMockAnalytics(selectedScenario, selectedEntity, selectedEntityGroup);
      const emptyEntityTable = generateMockEntityTable(selectedScenario, selectedEntity);

      setAnalytics(emptyAnalytics);
      setEntityTableData(emptyEntityTable);
      setLoading(false);
    }
  };

  // Generate  // Fetch data when scenario, entity, or prior scenario changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedScenario, selectedEntity, selectedEntityGroup, selectedPriorScenario]);

  const handleSort = (column) => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortBy(column);
    setSortOrder(isAsc ? 'desc' : 'asc');

    // Sort the entity table data
    const sortedData = [...entityTableData].sort((a, b) => {
      const aVal = a[column] || 0;
      const bVal = b[column] || 0;

      if (isAsc) {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    setEntityTableData(sortedData);
  };

  // Handle column visibility changes
  const handleColumnVisibilityChange = (columnKey, isVisible) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: isVisible
    };
    setVisibleColumns(newVisibleColumns);

    // Save to localStorage for persistence
    localStorage.setItem('dashboard-column-preferences', JSON.stringify(newVisibleColumns));
  };

  // Handle customize dialog
  const handleCustomizeDialogClose = () => {
    setCustomizeDialogOpen(false);
  };

  // Reset to default columns
  const handleResetToDefaults = () => {
    const defaultColumns = AVAILABLE_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.defaultVisible;
      return acc;
    }, {});
    setVisibleColumns(defaultColumns);
    localStorage.setItem('dashboard-column-preferences', JSON.stringify(defaultColumns));
  };

  // Handle dashboard view changes
  const handleDashboardViewChange = (newView) => {
    setDashboardView(newView);
    localStorage.setItem('dashboard-view-preference', newView);
  };

  // Handle analytics configuration changes
  const handleAnalyticsConfigChange = (newConfig) => {
    const updatedConfig = { ...analyticsConfig, ...newConfig };
    setAnalyticsConfig(updatedConfig);
    localStorage.setItem('dashboard-analytics-config', JSON.stringify(updatedConfig));
  };

  // Dashboard view options
  const DASHBOARD_VIEW_OPTIONS = [
  { key: 'entityTable', label: 'Entity Table View', description: 'Traditional table with customizable columns' },
  { key: 'etrWaterfall', label: 'ETR Waterfall Chart', description: 'Countries driving effective tax rate' },
  { key: 'pretaxWaterfall', label: 'Pretax Income Waterfall', description: 'Changes in pretax income by country' },
  { key: 'taxExpenseWaterfall', label: 'Tax Expense Waterfall', description: 'Changes in tax expense by country' },
  { key: 'yearComparison', label: '3-Year Comparison Table', description: 'Multi-year analysis by country/entity' },
  { key: 'taxRateChanges', label: 'Tax Rate Changes Summary', description: 'Entities with tax rate changes' },
  { key: 'stateTaxSummary', label: 'State Tax Summary', description: 'State tax expense breakdown' },
  { key: 'expandedJurisdictions', label: 'Expanded Jurisdictions', description: 'All countries analytics view' }];


  // Render table cell based on column type
  const renderTableCell = (entity, columnKey) => {
    const value = entity[columnKey];

    switch (columnKey) {
      case 'pretaxBookIncome':
      case 'totalTaxExpense':
      case 'currentTaxExpense':
      case 'deferredTaxExpense':
        return <TableCell align="right">{formatCurrency(value)}</TableCell>;
      case 'effectiveTaxRate':
        return <TableCell align="right">{formatPercentage(value)}</TableCell>;
      case 'allAdjustments':
      case 'temporaryAdjustments':
      case 'permanentAdjustments':
      case 'afterTaxAdjustments':
        return (
          <TableCell align="center">
            <Chip
              label={value}
              size="small"
              color={value > 10 ? "warning" : "default"} />

          </TableCell>);

      default:
        return <TableCell>{value}</TableCell>;
    }
  };

  // Generate ETR waterfall data
  const generateETRWaterfallData = () => {
    // Calculate domestic tax rate dynamically from EntityInput data
    let domesticRate = 0;
    if (entityInputs && entityInputs.length > 0) {
      const usEntity = entityInputs.find((ei) => ei.EntityCode?.includes('US') || ei.EntityCode === '001');
      if (usEntity) {
        const taxRate = usEntity.CurrentTaxRate || 0;
        domesticRate = taxRate > 1 ? taxRate : taxRate * 100;
      } else {
        const avgRate = entityInputs.reduce((sum, ei) => sum + (ei.CurrentTaxRate || 0), 0) / entityInputs.length;
        domesticRate = avgRate > 1 ? avgRate : avgRate * 100;
      }
    }
    const countries = [
    { name: 'United States', impact: 0, cumulative: domesticRate },
    { name: 'United Kingdom', impact: -2.3, cumulative: domesticRate - 2.3 },
    { name: 'Germany', impact: 1.8, cumulative: domesticRate - 2.3 + 1.8 },
    { name: 'Canada', impact: -0.9, cumulative: domesticRate - 2.3 + 1.8 - 0.9 },
    { name: 'France', impact: 0.7, cumulative: domesticRate - 2.3 + 1.8 - 0.9 + 0.7 },
    { name: 'Others', impact: -1.1, cumulative: domesticRate - 2.3 + 1.8 - 0.9 + 0.7 - 1.1 }].
    slice(0, analyticsConfig.etrWaterfallTopCountries + 1); // +1 for "Others"

    return countries;
  };

  // Render ETR Waterfall Chart
  const renderETRWaterfallChart = () => {
    const waterfallData = generateETRWaterfallData();
    const finalETR = waterfallData[waterfallData.length - 1].cumulative;
    const domesticRate = waterfallData[0].cumulative;

    return (
      <Box>
        {/* Chart Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Domestic Rate: {domesticRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
            Final ETR: {finalETR.toFixed(1)}%
          </Typography>
        </Box>

        {/* Waterfall Visualization */}
        <Box sx={{ position: 'relative', height: 320, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'end', height: 250, gap: 1, position: 'absolute', width: '100%' }}>
            {waterfallData.map((item, index) => {
              const isPositive = item.impact >= 0;
              const barHeight = Math.abs(item.impact) * 8 + 60; // Scale for visualization

              return (
                <Box key={item.name} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' }}>
                  {/* Value Label */}
                  <Typography variant="caption" sx={{ fontWeight: 'bold', position: 'absolute', top: -20 }}>
                    {item.cumulative.toFixed(1)}%
                  </Typography>
                  
                  {/* Bar */}
                  <Box
                    sx={{
                      width: '70%',
                      height: barHeight,
                      bgcolor: index === 0 ? '#1976d2' :
                      index === waterfallData.length - 1 ? '#2e7d32' :
                      isPositive ? '#d32f2f' : '#388e3c',
                      borderRadius: 1,
                      position: 'absolute',
                      bottom: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>

                    {/* Impact Label */}
                    {item.impact !== 0 &&
                    <Typography variant="caption" color="white" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {item.impact > 0 ? '+' : ''}{item.impact.toFixed(1)}%
                      </Typography>
                    }
                  </Box>
                  
                  {/* Country Label */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      width: '100%'
                    }}>

                    {item.name}
                  </Typography>
                </Box>);

            })}
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#1976d2', borderRadius: 1 }} />
            <Typography variant="caption">Starting Rate</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#388e3c', borderRadius: 1 }} />
            <Typography variant="caption">Decrease ETR</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#d32f2f', borderRadius: 1 }} />
            <Typography variant="caption">Increase ETR</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#2e7d32', borderRadius: 1 }} />
            <Typography variant="caption">Final ETR</Typography>
          </Box>
        </Box>
      </Box>);

  };

  // Render analytics view based on selected dashboard view
  const renderAnalyticsView = () => {
    switch (dashboardView) {
      case 'etrWaterfall':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ETR Waterfall Chart - Countries Driving Effective Tax Rate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing top {analyticsConfig.etrWaterfallTopCountries} countries with others grouped
              </Typography>
              {/* ETR Waterfall Chart Implementation */}
              <Box sx={{ height: 400, p: 2 }}>
                {renderETRWaterfallChart()}
              </Box>
            </CardContent>
          </Card>);


      case 'pretaxWaterfall':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pretax Book Income Changes - Waterfall by Country
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes from {analyticsConfig.priorPeriodScenario || 'prior period'} to current scenario
              </Typography>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  📊 Pretax Income Waterfall Chart (Chart library integration needed)
                </Typography>
              </Box>
            </CardContent>
          </Card>);


      case 'taxExpenseWaterfall':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Tax Expense Changes - Waterfall by Country
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes from {analyticsConfig.priorPeriodScenario || 'prior period'} to current scenario
              </Typography>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  📊 Tax Expense Waterfall Chart (Chart library integration needed)
                </Typography>
              </Box>
            </CardContent>
          </Card>);


      case 'yearComparison':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {analyticsConfig.comparisonYears}-Year Comparison by {analyticsConfig.comparisonGroupBy === 'country' ? 'Country' : 'Entity'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Metric: {analyticsConfig.comparisonMetric.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{analyticsConfig.comparisonGroupBy === 'country' ? 'Country' : 'Entity'}</TableCell>
                      <TableCell align="right">2023</TableCell>
                      <TableCell align="right">2024</TableCell>
                      <TableCell align="right">2025</TableCell>
                      <TableCell align="right">Change %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.topJurisdictions.slice(0, 5).map((jurisdiction, index) => {
                      const current = jurisdiction[analyticsConfig.comparisonMetric] || jurisdiction.pretaxIncome;
                      // Use real historical data if available, otherwise show N/A
                      const previous = jurisdiction.historical?.previous || null;
                      const previousYear = jurisdiction.historical?.previousYear || null;
                      const change = previous && current ? (current - previous) / previous * 100 : null;

                      return (
                        <TableRow key={jurisdiction.region}>
                          <TableCell>{jurisdiction.region}</TableCell>
                          <TableCell align="right">
                            {previousYear ? formatCurrency(previousYear) : 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            {previous ? formatCurrency(previous) : 'N/A'}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(current)}</TableCell>
                          <TableCell align="right" sx={{ color: change !== null ? change >= 0 ? 'success.main' : 'error.main' : 'text.secondary' }}>
                            {change !== null ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%` : 'N/A'}
                          </TableCell>
                        </TableRow>);

                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>);


      case 'taxRateChanges':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Entities with Tax Rate Changes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current tax rate or ending deferred tax rate changes
              </Typography>
              <Grid container spacing={2}>
                {entityTableData.slice(0, 4).map((entity, index) => {
                  const currentRate = entity.effectiveTaxRate;
                  // Use real historical tax rate data if available
                  const previousRate = entity.historical?.previousEffectiveTaxRate || null;
                  const currentDeferredRate = entity.deferredTaxExpense && entity.pretaxBookIncome ?
                  entity.deferredTaxExpense / entity.pretaxBookIncome * 100 :
                  null;
                  const previousDeferredRate = entity.historical?.previousDeferredTaxRate || null;

                  return (
                    <Grid item xs={12} sm={6} key={entity.id}>
                      <Paper sx={{ p: 2, bgcolor: index % 2 === 0 ? '#e8f5e8' : '#fff3e0' }}>
                        <Typography variant="subtitle2">{entity.entityName}</Typography>
                        <Typography variant="body2">
                          Current Rate: {previousRate ? `${previousRate.toFixed(1)}% → ` : ''}{currentRate.toFixed(1)}%
                          {!previousRate && <span style={{ color: '#666', fontSize: '0.8em' }}> (No historical data)</span>}
                        </Typography>
                        <Typography variant="body2">
                          Deferred Rate: {previousDeferredRate ? `${previousDeferredRate.toFixed(1)}% → ` : ''}
                          {currentDeferredRate ? `${currentDeferredRate.toFixed(1)}%` : 'N/A'}
                          {!previousDeferredRate && currentDeferredRate && <span style={{ color: '#666', fontSize: '0.8em' }}> (No historical data)</span>}
                        </Typography>
                      </Paper>
                    </Grid>);

                })}
              </Grid>
            </CardContent>
          </Card>);


      case 'stateTaxSummary':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                State Tax Expense Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Major states with detailed breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Current Tax</TableCell>
                      <TableCell align="right">Deferred Tax</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['California', 'New York', 'Texas', 'Florida', 'Illinois', 'Others'].map((state, index) => {
                      // Calculate state tax as portion of total US tax expense
                      const usJurisdiction = analytics.topJurisdictions.find((j) => j.region === 'United States');
                      const stateTaxBase = usJurisdiction ? usJurisdiction.taxExpense : analytics.totalTaxExpense.current;
                      const stateMultiplier = [0.25, 0.20, 0.15, 0.12, 0.10, 0.18][index] || 0.05;
                      const totalStateTax = stateTaxBase * stateMultiplier;
                      const currentTax = totalStateTax * 0.65;
                      const deferredTax = totalStateTax * 0.35;

                      return (
                        <TableRow key={state}>
                          <TableCell>{state}</TableCell>
                          <TableCell align="right">{formatCurrency(currentTax)}</TableCell>
                          <TableCell align="right">{formatCurrency(deferredTax)}</TableCell>
                          <TableCell align="right">{formatCurrency(totalStateTax)}</TableCell>
                        </TableRow>);

                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>);


      case 'expandedJurisdictions':
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Jurisdictions Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Expanded view showing all countries
              </Typography>
              <Grid container spacing={2}>
                {analytics.topJurisdictions.map((jurisdiction, index) =>
                <Grid item xs={12} sm={6} md={4} key={jurisdiction.region}>
                    <Paper sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <Typography variant="subtitle2">{jurisdiction.region}</Typography>
                      <Typography variant="body2">
                        Pretax Income: {formatCurrency(jurisdiction.pretaxIncome)}
                      </Typography>
                      <Typography variant="body2">
                        Tax Expense: {formatCurrency(jurisdiction.taxExpense)}
                      </Typography>
                      <Typography variant="body2">
                        ETR: {formatPercentage(jurisdiction.etr)}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>);


      default: // entityTable
        return (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Entity Details</Typography>
              <Button
                startIcon={<ViewColumnIcon />}
                onClick={() => setCustomizeDialogOpen(true)}
                variant="outlined"
                size="small">

                Customize Columns
              </Button>
            </Box>
            
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {AVAILABLE_COLUMNS.filter((col) => visibleColumns[col.key]).map((column) =>
                    <TableCell key={column.key} align={column.key.includes('Tax') || column.key === 'pretaxBookIncome' || column.key === 'effectiveTaxRate' ? 'right' : column.key.includes('Adjustments') ? 'center' : 'left'}>
                        <TableSortLabel
                        active={sortBy === column.key}
                        direction={sortBy === column.key ? sortOrder : 'asc'}
                        onClick={() => handleSort(column.key)}>

                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityTableData.map((entity) =>
                  <TableRow key={entity.id} hover>
                      {AVAILABLE_COLUMNS.filter((col) => visibleColumns[col.key]).map((column) =>
                    <React.Fragment key={column.key}>
                          {renderTableCell(entity, column.key)}
                        </React.Fragment>
                    )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>);

    }
  };

  // Remove loading state - always show dashboard with data or zeros

  if (!selectedScenario) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Please select a scenario to view dashboard analytics.
      </Alert>);

  }

  // Debug: Log the current analytics state




  return (
    <Box sx={{ p: 3 }}>

      {/* KPI Cards */}
      {analytics ?
      <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <AttachMoneyIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Pretax Book Income
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatCurrency(analytics.pretaxBookIncome.current)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {analytics.pretaxBookIncome.change >= 0 ?
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} /> :

                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  }
                    <Typography
                    variant="body2"
                    sx={{
                      color: analytics.pretaxBookIncome.change >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(Math.abs(analytics.pretaxBookIncome.change))} {analytics.pretaxBookIncome.change >= 0 ? 'increase' : 'decrease'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatCurrency(Math.abs(analytics.pretaxBookIncome.changeAmount))} vs. previous scenario
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <AssessmentIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Total Tax Expense
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatCurrency(analytics.totalTaxExpense.current)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {analytics.totalTaxExpense.change >= 0 ?
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} /> :

                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  }
                    <Typography
                    variant="body2"
                    sx={{
                      color: analytics.totalTaxExpense.change >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(Math.abs(analytics.totalTaxExpense.change))} {analytics.totalTaxExpense.change >= 0 ? 'increase' : 'decrease'}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatCurrency(Math.abs(analytics.totalTaxExpense.changeAmount))} vs. previous scenario
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <TrendingUpIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Effective Tax Rate
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatPercentage(analytics.effectiveTaxRate.current)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {analytics.effectiveTaxRate.change >= 0 ?
                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} /> :

                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  }
                    <Typography
                    variant="body2"
                    sx={{
                      color: analytics.effectiveTaxRate.change >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(Math.abs(analytics.effectiveTaxRate.change))} {analytics.effectiveTaxRate.change >= 0 ? 'increase' : 'decrease'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <BusinessIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Active Entities
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {analytics.entityCount}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon sx={{ color: 'info.main', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      {analytics.jurisdictionCount} Jurisdictions
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tax Expense Breakdown */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Tax Expense Breakdown
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Current Tax Expense</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(analytics.totalTaxExpense.breakdown.currentTaxExpense)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Deferred Tax Expense</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(analytics.totalTaxExpense.breakdown.deferredTaxExpense)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(analytics.totalTaxExpense.current)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Period Comparison
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Current Period</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(analytics.pretaxBookIncome.current)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Previous Scenario</Typography>
                      <Typography variant="body2">
                        {formatCurrency(analytics.pretaxBookIncome.previous)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Change</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {analytics.pretaxBookIncome.change >= 0 ?
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} /> :

                      <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
                      }
                        <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          color: analytics.pretaxBookIncome.change >= 0 ? 'success.main' : 'error.main'
                        }}>

                          {formatPercentage(Math.abs(analytics.pretaxBookIncome.change))}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Dashboard View Selection */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              {DASHBOARD_VIEW_OPTIONS.find((option) => option.key === dashboardView)?.label || 'Dashboard View'}
            </Typography>
            {/* Customize Dashboard button hidden for now */}
            {/* <Button
             startIcon={<DashboardIcon />}
             onClick={() => setDashboardViewDialogOpen(true)}
             variant="outlined"
             size="small"
            >
             Customize Dashboard
            </Button> */}
          </Box>

          {/* Main Content Area */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={dashboardView === 'expandedJurisdictions' ? 12 : 8}>
              {renderAnalyticsView()}
            </Grid>

            {/* Jurisdiction Analytics Sidebar - only show if not in expanded view */}
            {dashboardView !== 'expandedJurisdictions' &&
          <Grid item xs={12} lg={4}>
                <Paper sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Top Jurisdictions</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                    value={analyticsView}
                    onChange={(e) => setAnalyticsView(e.target.value)}>

                        <MenuItem value="pretaxIncome">Pretax Income</MenuItem>
                        <MenuItem value="taxExpense">Tax Expense</MenuItem>
                        <MenuItem value="etr">Effective Tax Rate</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {analytics.topJurisdictions.slice(0, 10).map((jurisdiction, index) =>
              <Box
                key={jurisdiction.region}
                sx={{
                  p: 1,
                  mb: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  backgroundColor: selectedJurisdiction === jurisdiction.region ? '#e3f2fd' : 'transparent'
                }}
                onClick={() => {
                  if (selectedJurisdiction === jurisdiction.region) {
                    setSelectedJurisdiction(null);
                  } else {
                    setSelectedJurisdiction(jurisdiction.region);
                  }
                }}>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="medium">
                          {index + 1}. {jurisdiction.region}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {analyticsView === 'pretaxIncome' && formatCurrency(jurisdiction.pretaxIncome)}
                          {analyticsView === 'taxExpense' && formatCurrency(jurisdiction.taxExpense)}
                          {analyticsView === 'etr' && formatPercentage(jurisdiction.etr)}
                        </Typography>
                      </Box>
                    </Box>
              )}
                </Paper>
              </Grid>
          }
          </Grid>

          {/* Column Customization Dialog */}
          <Dialog open={customizeDialogOpen} onClose={handleCustomizeDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>Customize Table Columns</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which columns to display in the entity table:
              </Typography>
              {AVAILABLE_COLUMNS.map((column) =>
            <FormControlLabel
              key={column.key}
              control={
              <Checkbox
                checked={visibleColumns[column.key] || false}
                onChange={(e) => handleColumnVisibilityChange(column.key, e.target.checked)} />

              }
              label={column.label}
              sx={{ display: 'block', mb: 1 }} />

            )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleResetToDefaults} color="secondary">
                Reset to Defaults
              </Button>
              <Button onClick={handleCustomizeDialogClose} color="primary" variant="contained">
                Done
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dashboard View Customization Dialog */}
          <Dialog open={dashboardViewDialogOpen} onClose={() => setDashboardViewDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Customize Dashboard View</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose how you want to view your dashboard data:
              </Typography>
              
              <RadioGroup
              value={dashboardView}
              onChange={(e) => handleDashboardViewChange(e.target.value)}>

                {DASHBOARD_VIEW_OPTIONS.map((option) =>
              <FormControlLabel
                key={option.key}
                value={option.key}
                control={<Radio />}
                label={
                <Box>
                        <Typography variant="subtitle2">{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }} />

              )}
              </RadioGroup>

              {/* Configuration options based on selected view */}
              {dashboardView === 'etrWaterfall' &&
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>ETR Waterfall Settings</Typography>
                  <Typography variant="body2" gutterBottom>
                    Top countries to show: {analyticsConfig.etrWaterfallTopCountries}
                  </Typography>
                  <Slider
                value={analyticsConfig.etrWaterfallTopCountries}
                onChange={(e, value) => handleAnalyticsConfigChange({ etrWaterfallTopCountries: value })}
                min={3}
                max={15}
                step={1}
                marks
                valueLabelDisplay="auto" />

                </Box>
            }

              {(dashboardView === 'pretaxWaterfall' || dashboardView === 'taxExpenseWaterfall') &&
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Waterfall Settings</Typography>
                  <TextField
                label="Prior Period Scenario"
                value={analyticsConfig.priorPeriodScenario || ''}
                onChange={(e) => handleAnalyticsConfigChange({ priorPeriodScenario: e.target.value })}
                fullWidth
                size="small"
                placeholder="Enter scenario name for comparison" />

                </Box>
            }

              {dashboardView === 'yearComparison' &&
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>3-Year Comparison Settings</Typography>
                  
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>Comparison Metric:</Typography>
                    <Select
                  value={analyticsConfig.comparisonMetric}
                  onChange={(e) => handleAnalyticsConfigChange({ comparisonMetric: e.target.value })}>

                      <MenuItem value="pretaxBookIncome">Pretax Book Income</MenuItem>
                      <MenuItem value="totalTaxExpense">Total Tax Expense</MenuItem>
                      <MenuItem value="currentTaxExpense">Current Tax Expense</MenuItem>
                      <MenuItem value="deferredTaxExpense">Deferred Tax Expense</MenuItem>
                      <MenuItem value="totalPermAdjustments">Total Permanent Adjustments</MenuItem>
                      <MenuItem value="totalTempAdjustments">Total Temporary Adjustments</MenuItem>
                      <MenuItem value="currentTaxRate">Current Tax Rate</MenuItem>
                      <MenuItem value="endingDeferredTaxRate">Ending Deferred Tax Rate</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <Typography variant="body2" gutterBottom>Group By:</Typography>
                    <Select
                  value={analyticsConfig.comparisonGroupBy}
                  onChange={(e) => handleAnalyticsConfigChange({ comparisonGroupBy: e.target.value })}>

                      <MenuItem value="country">Country</MenuItem>
                      <MenuItem value="entity">Entity</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
            }

              {dashboardView === 'stateTaxSummary' &&
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>State Tax Settings</Typography>
                  <FormControlLabel
                control={
                <Switch
                  checked={analyticsConfig.showStateDetails}
                  onChange={(e) => handleAnalyticsConfigChange({ showStateDetails: e.target.checked })} />

                }
                label="Show detailed state breakdown" />

                </Box>
            }
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDashboardViewDialogOpen(false)} color="primary" variant="contained">
                Apply Changes
              </Button>
            </DialogActions>
          </Dialog>
        </> :

      // Show dashboard with zero values when no analytics data
      <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <AttachMoneyIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Pretax Book Income
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatCurrency(0)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography
                    variant="body2"
                    sx={{
                      color: 'success.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(0)} increase
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatCurrency(0)} vs. previous scenario
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <AssessmentIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Total Tax Expense
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatCurrency(0)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography
                    variant="body2"
                    sx={{
                      color: 'success.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(0)} increase
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatCurrency(0)} vs. previous scenario
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <TrendingUpIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Effective Tax Rate
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {formatPercentage(0)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography
                    variant="body2"
                    sx={{
                      color: 'success.main',
                      fontWeight: 'medium'
                    }}>

                      {formatPercentage(0)} increase
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatPercentage(0)} vs. previous scenario
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      <BusinessIcon />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      Entity Count
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    0
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    Active entities
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Entity Table with empty state */}
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }}>
                <TableHead>
                  <TableRow>
                    {AVAILABLE_COLUMNS.filter((col) => visibleColumns[col.key]).map((column) =>
                  <TableCell key={column.key}>
                        <TableSortLabel
                      active={sortBy === column.key}
                      direction={sortBy === column.key ? sortOrder : 'asc'}
                      onClick={() => handleSort(column.key)}>

                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                  )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={AVAILABLE_COLUMNS.filter((col) => visibleColumns[col.key]).length} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No data available for the selected scenario
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      }
    </Box>);

}

export default Dashboard;
